import { Col, Flex, Tooltip } from 'antd';
import React from 'react';
import { LogiblocksIcon } from '../../../assets/icons/icons';
import { CustomParagraph } from '../../../components';
import { AlertNestedSms, Recipient } from '@/apis/types';
import { COCFormattedTime } from './OneTimeCOCAlert';

interface AlertNestedSmsRowProps {
  data: AlertNestedSms;
  getFormattedTime: (timestamp: string) => COCFormattedTime;
  showFull: boolean;
  setShowFull: React.Dispatch<React.SetStateAction<boolean>>;
}

const RecipientsDisplay: React.FC<{ recipients?: Recipient[] }> = ({
  recipients = [],
}) => {
  if (recipients.length === 0) return null;

  if (recipients.length === 1) {
    return (
      <span>
        {recipients[0]?.name}
        {recipients[0]?.role && ` (${recipients[0]?.role})`}
      </span>
    );
  }

  const [first, ...rest] = recipients;
  return (
    <span>
      {first?.name}
      {first?.role && ` (${first?.role})`}
      <Tooltip
        title={rest.map((r) => (
          <div key={r?.id ?? r?.name}>
            {r?.name}
            {r?.role && ` (${r?.role})`}
          </div>
        ))}
      >
        <span className='cursor-pointer'>+{rest.length}</span>
      </Tooltip>
    </span>
  );
};

const AlertNestedSmsRow: React.FC<AlertNestedSmsRowProps> = ({
  data,
  getFormattedTime,
  showFull,
  setShowFull,
}) => {
  const title =
    data.title ??
    (data.is_inbound ? 'Automation: SMS (Received)' : 'Automation: SMS (Sent)');

  return (
    <Flex
      gap={12}
      style={{
        marginTop: '12px',
        marginBottom: '12px',
      }}
    >
      {/* Same icon column as the alert's other entries (CoC_CT), so every row's
          text starts at the same place. */}
      <Col
        span={1}
        style={{
          display: 'flex',
          justifyContent: 'start',
          marginRight: '20px',
        }}
      >
        <Flex
          align='center'
          justify='center'
          data-testid='alert-nested-sms-icon'
          style={{
            backgroundColor: 'var(--gray-600)',
            borderRadius: '50%',
            width: '36px',
            height: '36px',
            flexShrink: 0,
          }}
        >
          <LogiblocksIcon size={16} color='white' />
        </Flex>
      </Col>
      <Flex vertical justify='space-between' style={{ width: '100%' }}>
        <Flex justify='space-between' style={{ height: '100%' }}>
          <Tooltip title={title}>
            <Flex align='center' style={{ height: '100%' }}>
              <CustomParagraph
                text={title}
                fontSize='12px'
                fontWeight={600}
                lineHeight='16px'
                color='var(--text-primary)'
                marginBottom={0}
                whiteSpace='pre-line'
              />
            </Flex>
          </Tooltip>
          {data.timestamp && (
            <Flex
              align='center'
              onClick={() => setShowFull((prev) => !prev)}
              style={{
                cursor: 'pointer',
                height: '100%',
              }}
            >
              <CustomParagraph
                text={
                  showFull
                    ? getFormattedTime(data.timestamp)?.full
                    : getFormattedTime(data.timestamp)?.relative
                }
                fontSize='12px'
                fontWeight={400}
                lineHeight='16px'
                color='var(--text-light)'
                marginBottom='0'
                style={{ whiteSpace: 'nowrap' }}
              />
            </Flex>
          )}
        </Flex>
        <p className='zl-para-12 coc-comment-editor mt-1'>
          Triggered by {data.triggered_by} →{' '}
          <RecipientsDisplay recipients={data.recipients} />
        </p>
        {data.message && (
          <p className='mt-2 color-black coc-comment-editor zl-para-12'>
            Message: {data.message}
          </p>
        )}
      </Flex>
    </Flex>
  );
};

export default AlertNestedSmsRow;
